import { useSelector } from 'react-redux';
import { LayoutType, WaveType } from 'signup-components/types';
import { Step } from 'signup-constants/Step';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { LoadingKey } from '../app-active-loading/LoadingKey';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { useMessage } from '../messaging/useMessage';
import { getActiveError } from '../store/error/selectors';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { getActiveLoading } from '../store/loading-status/selectors';
import { getStepConfig } from '../views/getStepConfig';
import { useCurrentStep } from './useCurrentStep';
import { useIsFramedCentredModalStep } from './useIsFramedCerntredModalStep';
import { useInSidePanelLayoutExperiment } from './useInSidePanellayoutExperiment';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { useExperiment, TrackWhen } from '../experiments/useExperiment';
import { ErrorKey } from '../app-active-error/ErrorKey';
export const getWaveType = ({
  currentStep,
  postAccountCreationSteps,
  activeLoading,
  stepConfig
}) => {
  const isPostAccountCreationStep = postAccountCreationSteps.includes(currentStep);
  if (activeLoading || !stepConfig) {
    return undefined;
  }
  if (isPostAccountCreationStep && getFlowConfig().shouldUseWaveOnPostAccountCreation || currentStep === Step.InstallSalesExtension) {
    return WaveType.LowOrange;
  }
  return stepConfig.waveType;
};
export const useLayoutTemplate = () => {
  const postAccountCreationSteps = useSelector(getPostAccountCreationSteps);
  const {
    currentStep
  } = useCurrentStep();
  const activeError = useSelector(getActiveError);
  const step = activeError || currentStep;
  const stepConfig = getStepConfig(step);
  const {
    isActive: isInstanceOfSignupEmbedder
  } = useSignupEmbedder();
  const {
    isFramedCentredModalStep
  } = useIsFramedCentredModalStep();
  const showMessagingAboveContent = stepConfig && stepConfig.showMessagingAboveContent;
  const {
    shouldDisplayMessaging
  } = useMessage();
  const {
    isInSidePanelLayoutExperiment
  } = useInSidePanelLayoutExperiment();
  const {
    isAssignedToSpecificVariant: isAssignedToSpecificVariantACQ173
  } = useExperiment(ExperimentKeys.ACQ0173, TrackWhen.never);
  const isAssignedToACQ173VariantA = isAssignedToSpecificVariantACQ173(ExperimentParameterGroup.Variant_A);
  const isAssignedToACQ173VariantB = isAssignedToSpecificVariantACQ173(ExperimentParameterGroup.Variant_B);
  const isAssignedToACQ173VariantC = isAssignedToSpecificVariantACQ173(ExperimentParameterGroup.Variant_C);
  const {
    isAssignedToSpecificVariant: isAssignedToACQ172VariantC
  } = useExperiment(ExperimentKeys.ACQ0172, TrackWhen.never);
  const layoutType = shouldDisplayMessaging && !showMessagingAboveContent ? LayoutType.TwoColumn : getFlowConfig().getLayoutType();
  const activeLoading = useSelector(getActiveLoading);
  const waveType = getWaveType({
    currentStep,
    postAccountCreationSteps,
    activeLoading,
    stepConfig
  });
  const isPostAccountCreationStep = postAccountCreationSteps.includes(currentStep);
  const isNotVerificationLoading = activeLoading !== LoadingKey.InitiateVerificationRequest;
  const stepConfigHasProgressBar = stepConfig && stepConfig.stepKey && !stepConfig.hideProgressBar;
  let shouldShowProgressBar = isNotVerificationLoading && !activeError && stepConfigHasProgressBar;
  const hideSidePanelOnLandingPage = step === Step.LandingPage && (isAssignedToACQ172VariantC(ExperimentParameterGroup.Variant_C) || isAssignedToACQ173VariantA || isAssignedToACQ173VariantB || isAssignedToACQ173VariantC);
  if (isInSidePanelLayoutExperiment) {
    shouldShowProgressBar = false;
  }
  if (isFramedCentredModalStep) {
    return {
      layoutType: LayoutType.FramedCentredModalStep,
      waveType,
      shouldShowProgressBar
    };
  }
  if (activeLoading) {
    return {
      layoutType: LayoutType.CentredStep,
      waveType,
      shouldShowProgressBar
    };
  }
  if (isPostAccountCreationStep && !isInstanceOfSignupEmbedder) {
    return {
      layoutType,
      waveType,
      shouldShowProgressBar
    };
  }
  if (isAssignedToACQ173VariantB && activeError && activeError === ErrorKey.RecaptchaCreationError) {
    return {
      layoutType: LayoutType.SidePanel,
      waveType,
      shouldShowProgressBar
    };
  }
  if (isInSidePanelLayoutExperiment && !activeError && !hideSidePanelOnLandingPage) {
    return {
      layoutType: LayoutType.SidePanel,
      waveType,
      shouldShowProgressBar
    };
  }
  return {
    layoutType,
    waveType,
    shouldShowProgressBar
  };
};